import type { AppErrorOptions } from './AppErrorOptions';
import { AppErrorType } from './AppErrorType';

/**
 * application error model
 */
export class AppError extends Error {
  /**
   * application unique error code
   */
  public readonly code: string;

  /**
   * associated error data
   */
  public readonly data: Record<string, unknown> | undefined;

  /**
   * error type
   */
  public readonly type: AppErrorType;

  /**
   * creates a new instance
   * @param options the error options
   */
  public constructor(options: AppErrorOptions) {
    super(options.message, { cause: options.cause });

    // maintains proper stack trace (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }

    this.name = 'AppError';
    this.code = options.code;
    this.data = options.data;
    this.type = options.type;
  }

  public toJSON(): unknown {
    return {
      name: this.name,
      code: this.code,
      type: this.type,
      data: this.data,
      cause: this.cause,
    };
  }

  public toSerialised(): AppErrorSerialised {
    return {
      code: this.code,
      message: this.message,
      type: this.type,
      data: JSON.stringify(this.data),
      cause: JSON.stringify(this.cause),
    };
  }
}

export type AppErrorSerialised = {
  code: string;
  message: string;
  type: AppErrorType;
  data?: string;
  cause?: string;
};
